import { DateTime, Zone } from 'luxon';

import {
  SIMPLE_DATE,
  LONG_MONTH_DATE_YEAR,
  SHORT_DATE,
  SHORT_DATE_FULL_MONTH,
  RECENT_DATE_V2,
  RECENT_DATE_FULL_MONTH,
  SHORT_MONTH,
  LONG_MONTH,
  SHORT_DAY,
  LONG_DAY,
  LONG_DATE_TIME,
  SHORT_TIME,
} from '../constants/dates';
import parseDateTime from '../dates/parseDateTime';

const DATE_FORMAT_TOKEN_REGEX = /(\w)\1+|\w/g;

export default function dateFormatter(
  date: Date | DateTime | string | undefined | null,
  toFormat: string,
  timeZone?: string | Zone,
  locale?: string
): string {
  const dateTime = parseDateTime(date, timeZone);

  if (!dateTime || dateTime.invalidExplanation) {
    return '';
  }

  if (locale != null) {
    try {
      return dateFormat(toFormat, dateTime.setLocale(locale));
    } catch (err) {
      console.log(err);
    }
  }

  return dateTime.toFormat(toFormat);
}

// For Chinese locales, manually format dates because Android/Hermes doesn't have
// ICU locale data and toLocaleString() falls back to English
function formatChineseDate(fmt: string, localDate: DateTime): string {
  const getChineseMonth = () => `${localDate.month}月`;
  const getChineseDay = () => `${localDate.day}日`;
  const getChineseYear = () => `${localDate.year}年`;

  const getChineseWeekday = (short = false) => {
    const weekdays = short
      ? ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      : ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    const dayOfWeek = localDate.weekday % 7;

    return weekdays[dayOfWeek === 7 ? 0 : dayOfWeek];
  };

  // Direct pattern matching for date formats used in the app
  const patterns: Record<string, () => string> = {
    [SIMPLE_DATE]: () =>
      `${getChineseYear()}${getChineseMonth()}${getChineseDay()}`,
    [LONG_MONTH_DATE_YEAR]: () =>
      `${getChineseYear()}${getChineseMonth()}${getChineseDay()}`,

    [SHORT_DATE]: () => `${getChineseMonth()}${getChineseDay()}`,
    [SHORT_DATE_FULL_MONTH]: () => `${getChineseMonth()}${getChineseDay()}`,

    [RECENT_DATE_V2]: () =>
      `${getChineseWeekday()}, ${getChineseMonth()}${getChineseDay()}`,
    [RECENT_DATE_FULL_MONTH]: () =>
      `${getChineseWeekday()}, ${getChineseMonth()}${getChineseDay()}`,

    // Set locale to 'en' for time formatting to ensure consistent AM/PM on iOS
    [LONG_DATE_TIME]: () =>
      `${getChineseYear()}${getChineseMonth()}${getChineseDay()} ${getChineseWeekday(true)} - ${localDate.setLocale('en').toFormat(SHORT_TIME)}`,

    [SHORT_MONTH]: getChineseMonth,
    [LONG_MONTH]: getChineseMonth,
    LLL: getChineseMonth,
    LLLL: getChineseMonth,
    [SHORT_DAY]: () => getChineseWeekday(true),
    ccc: () => getChineseWeekday(true),
    [LONG_DAY]: getChineseWeekday,
    cccc: getChineseWeekday,
  };

  if (patterns[fmt]) {
    return patterns[fmt]();
  }

  return fmt.replace(DATE_FORMAT_TOKEN_REGEX, match => {
    if (patterns[match]) {
      return patterns[match]();
    }

    // Fall back to Luxon for other tokens (like time, timezone, etc.)
    return localDate.toFormat(match);
  });
}

function dateFormat(fmt: string, date: DateTime): string {
  const localDate = date || DateTime.local();
  const locale = localDate.locale || '';

  if (locale.startsWith('zh')) {
    return formatChineseDate(fmt, localDate);
  }

  return fmt.replace(DATE_FORMAT_TOKEN_REGEX, function (match) {
    switch (match) {
      case 'EEE':
      case 'ccc':
        return localDate.toLocaleString({ weekday: 'short' });
      case 'EEEE':
      case 'cccc':
        return localDate.toLocaleString({ weekday: 'long' });
      case 'MMM':
      case 'LLL':
        return localDate.toLocaleString({ month: 'short' });
      case 'MMMM':
      case 'LLLL':
        return localDate.toLocaleString({ month: 'long' });
      default:
        return localDate.toFormat(match);
    }
  });
}
